// Public, non-sensitive founder profile data.
//
// NOTE: email addresses are intentionally NOT in this file. They live
// server-side (artifacts/api-server/src/lib/founder-emails.ts) and are only
// returned after Cloudflare Turnstile verification — see FounderEmailReveal.tsx.
// Keeping them out of the client bundle is the whole point of the
// scraper-protection ask.
//
// Background content (career timeline, education, focus areas, fun facts) is
// drawn from each founder's public profiles — LinkedIn, personal site, and
// GitHub. The Pax Magellanic role/title is first-party.

export type FounderSocials = {
  linkedin?: string;
  x?: string;
  github?: string;
  website?: string;
};

export type TimelineStop = {
  label: string; // year or phase, e.g. "2024 →", "20+ yrs"
  title: string;
  detail: string;
};

export type FunFact = {
  label: string; // short mono kicker, e.g. "// toolkit"
  value: string;
};

export type Founder = {
  slug: string;
  name: string;
  firstName: string;
  role: string;
  tag: string; // short mono badge on the portrait, e.g. "// CIO"
  lede: string;
  narrative: string[];
  focus: string[];
  timeline: TimelineStop[];
  funFacts: FunFact[];
  socials: FounderSocials;
  // Image basenames in /public/assets — referenced as
  // `${import.meta.env.BASE_URL}assets/${img}-600.webp` etc.
  img: string;
  imgWidth: number;
  imgHeight: number;
};

export const FOUNDERS: Record<string, Founder> = {
  joe: {
    slug: "joe",
    name: "Joe Johnson",
    firstName: "Joe",
    role: "Chief Ideas Officer",
    tag: "// CIO",
    lede:
      "Two decades turning emerging technology into operating advantage — from cloud and security leadership to M&A integration, now focused on bringing AI to enterprises and SMBs.",
    narrative: [
      "Joe is a Houston-based technology executive with two decades spanning cloud operations, data and analytics, IT leadership, and complex M&A integration — and a deep focus on AI enablement for enterprise and SMB.",
      "His work pairs strategy with hands-on engineering. He's fluent in cloud-native infrastructure — Kubernetes, containerization, GitOps, and Azure — and grounds his security thinking in formal controls frameworks. He studied at Columbia College Chicago, graduating Summa Cum Laude, with executive education at the Wharton School.",
      "At Pax Magellanic he serves as Chief Ideas Officer, leading idea-to-architecture: reading where a business is headed and pairing that with the discipline to ship automation that actually sticks. A longtime supporter of the arts and nonprofit causes, he brings a people-first lens to the technology he builds.",
    ],
    focus: [
      "AI enablement",
      "Cloud operations",
      "M&A integration",
      "Cybersecurity & compliance",
      "Kubernetes & GitOps",
      "Data & analytics",
    ],
    timeline: [
      {
        label: "2024 →",
        title: "Co-founded Pax Magellanic",
        detail:
          "Chief Ideas Officer. Leads idea-to-architecture for SMB and enterprise AI adoption from Houston, TX.",
      },
      {
        label: "2021 → 2026",
        title: "VP, Cloud Operations — Revalize",
        detail:
          "Led cloud operations through scale and integration, with deep work in modernization and shift-left practices.",
      },
      {
        label: "2014 → 2021",
        title: "Director, IT — Call One & ServerCentral",
        detail:
          "Ran IT and infrastructure leadership across two organizations, anchoring security and operational discipline.",
      },
      {
        label: "20+ yrs",
        title: "Technology, security & M&A",
        detail:
          "Two decades of hands-on cloud, cybersecurity, and complex-integration work across the US.",
      },
    ],
    funFacts: [
      {
        label: "// toolkit",
        value:
          "Lives in Kubernetes — his GitHub bio is literally “k8s for everyone!” and he self-hosts a live-location web app on Azure Kubernetes Service via GitOps.",
      },
      {
        label: "// exploring",
        value:
          "Tinkers with generative-AI media tooling, including a ComfyUI image/video gallery for organizing AI-generated work.",
      },
      {
        label: "// off the clock",
        value:
          "A Mensa member with a long history in arts and nonprofit leadership — from producing at the Manifest Urban Arts Festival to chairing a foundation that directed ~$9.7M to good causes.",
      },
    ],
    socials: {
      linkedin: "https://www.linkedin.com/in/josephjohnson/",
      x: "https://x.com/jmfjyo",
      github: "https://github.com/hikarukujo",
      website: "https://jomofojo.co",
    },
    img: "joe",
    imgWidth: 1200,
    imgHeight: 1607,
  },
  andy: {
    slug: "andy",
    name: "Andy Dai",
    firstName: "Andy",
    role: "Chief AI Officer",
    tag: "// CAIO",
    lede:
      "A decade of data analysis, financial modeling, and reporting — now channeling that rigor into applied AI and automation at Pax Magellanic.",
    narrative: [
      "Andy Dai (Tianhui Dai) is a data and finance specialist — more than a decade across business- and data-analyst roles in data analysis, modeling, and reporting, most recently in a role associated with Ryder System.",
      "His foundation is quantitative finance: graduate finance coursework at IIT's Stuart School of Business — where his projects ranged from applying option-pricing methods to power-plant investment to using regression analysis for fund valuation and portfolio management — and a full-ride, 3.94-GPA program at the University of Bridgeport.",
      "At Pax Magellanic he serves as Chief AI Officer, turning that analytical discipline toward the technical core of each engagement — data engineering, AI automation, and generative media — and grounding it in the habit of making messy, real-world numbers legible.",
    ],
    focus: [
      "Data engineering",
      "Financial modeling",
      "AI automation",
      "Quantitative finance",
      "Generative media",
      "Logistics",
    ],
    timeline: [
      {
        label: "2024 →",
        title: "Co-founded Pax Magellanic",
        detail:
          "Chief AI Officer. Owns data engineering and applied-AI delivery for every engagement.",
      },
      {
        label: "Decade+",
        title: "Business & data analyst",
        detail:
          "Years of business- and data-analyst work in data analysis, modeling, and reporting — most recently associated with Ryder System.",
      },
      {
        label: "Education",
        title: "Finance & data foundation",
        detail:
          "Graduate finance coursework at IIT's Stuart School of Business and a full-ride, 3.94-GPA program at the University of Bridgeport (2017–2019).",
      },
      {
        label: "Foundation",
        title: "Quantitative projects",
        detail:
          "Academic work applying option pricing to physical-asset investment and regression analysis to fund valuation and portfolio management.",
      },
    ],
    funFacts: [
      {
        label: "// languages",
        value:
          "Speaks four — English, Mandarin Chinese, the Wenzhou dialect, and professional-level Japanese.",
      },
      {
        label: "// off the clock",
        value:
          "Served on the Junior Board of the Chicago Center for Music Education, supporting fundraising and community outreach.",
      },
      {
        label: "// honors",
        value:
          "Earned a National Scholarship from China's Ministry of Education and a merit scholarship from IIT's Stuart School of Business.",
      },
    ],
    socials: {
      linkedin: "https://www.linkedin.com/in/tianhuidai/",
      website: "https://daitianhui.com",
    },
    img: "andy",
    imgWidth: 1200,
    imgHeight: 1609,
  },
};

export const founderSlugs = Object.keys(FOUNDERS);

export function getFounder(slug: string | undefined): Founder | undefined {
  if (!slug) return undefined;
  return FOUNDERS[slug.toLowerCase()];
}
